import React from "react";
import { Card, CardBody, Button, Skeleton, Spinner } from "@heroui/react";
import { LogOut } from "lucide-react";
import { NetworkType } from "@/components/BridgeWallet";
import { IMAGES } from "@/localConstants/images";
import { truncateAddress } from "@/utils";
import { CopyButton } from "@/components/ui/copy-button";
import { BridgeAmountInput } from "./BridgeAmountInput";

interface BridgeNetworkCardProps {
  network: NetworkType;
  type: "from" | "to";
  amount?: string;
  isConnected: boolean;
  walletAddress: string;
  currentBalance: string;
  isBalanceLoading: boolean;
  onDisconnect: () => void;
  // From card specific props
  amountValue?: string;
  onAmountChange?: (value: string) => void;
  onPercentageClick?: (percentage: number) => void;
  hasInsufficientBalance?: boolean;
  isBelowMinimum?: boolean;
  isAboveMaximum?: boolean;
  limits?: any;
  limitsError?: any;
  // To card specific props
  isEstimating?: boolean;
}

export const BridgeNetworkCard: React.FC<BridgeNetworkCardProps> = ({
  network,
  type,
  amount: displayAmount,
  isConnected,
  walletAddress,
  currentBalance,
  isBalanceLoading,
  onDisconnect,
  amountValue,
  onAmountChange,
  onPercentageClick,
  hasInsufficientBalance,
  isBelowMinimum,
  isAboveMaximum,
  limits,
  limitsError,
  isEstimating,
}) => {
  const networkName = network === "multiversx" ? "MultiversX" : "Solana";
  const logo =
    network === "solana" ? IMAGES.SOLANA_LOGO : IMAGES.MULTIVERSX_LOGO;

  // Special layout for "from" card
  if (type === "from") {
    return (
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 font-medium font-roboto">From</span>
            {isConnected && walletAddress && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 font-mono">
                  ({truncateAddress(walletAddress)})
                </span>
                <CopyButton text={walletAddress} size="sm" />
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="text-gray-400 hover:text-red-400 min-w-unit-8 w-8 h-8 bg-transparent border-0"
                  onPress={onDisconnect}
                >
                  <LogOut size={14} />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Card */}
        <Card className="bg-neutral-800/50 backdrop-blur-md border border-neutral-700/50">
          <CardBody className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            {/* Top Section: Network and Balance */}
            <div className="flex items-center justify-between">
              {/* Network */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-full flex items-center justify-center">
                  <img
                    src={logo}
                    alt={networkName}
                    className={network === "multiversx" ? "w-5 h-5 sm:w-6 sm:h-6" : "w-6 h-6 sm:w-8 sm:h-8"}
                  />
                </div>
                <span className="text-white font-semibold font-roboto text-sm sm:text-base">
                  {networkName}
                </span>
              </div>

              {/* Available Balance */}
              {isConnected && (
                <div className="text-right">
                  <div className="text-xs text-gray-400 font-roboto">
                    Available balance
                  </div>
                  <div className="text-xs sm:text-sm text-white font-roboto">
                    {isBalanceLoading ? (
                      <Skeleton className="h-3 sm:h-4 w-12 sm:w-16 rounded bg-neutral-700" />
                    ) : (
                      `${currentBalance} A1X`
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Amount Input */}
            {onAmountChange && onPercentageClick && (
              <BridgeAmountInput
                amount={amountValue || ""}
                onAmountChange={onAmountChange}
                onPercentageClick={onPercentageClick}
                isConnected={isConnected}
                hasInsufficientBalance={hasInsufficientBalance || false}
                isBelowMinimum={isBelowMinimum || false}
                isAboveMaximum={isAboveMaximum || false}
                currentBalance={currentBalance}
                limits={limits}
                limitsError={limitsError}
              />
            )}
          </CardBody>
        </Card>
      </div>
    );
  }

  // Layout for "to" card
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 font-medium font-roboto">To</span>
          {isConnected && walletAddress && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 font-mono">
                ({truncateAddress(walletAddress)})
              </span>
              <CopyButton text={walletAddress} size="sm" />
              <Button
                isIconOnly
                size="sm"
                variant="light"
                className="text-gray-400 hover:text-red-400 min-w-unit-8 w-8 h-8 bg-transparent border-0"
                onPress={onDisconnect}
              >
                <LogOut size={14} />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Card */}
      <Card className="bg-neutral-800/50 backdrop-blur-md  border border-neutral-700/50">
        <CardBody className="p-3 sm:p-4">
          {/* Network and Amount Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-full flex items-center justify-center">
                <img
                  src={logo}
                  alt={networkName}
                  className={
                    network === "multiversx" ? "w-5 h-5 sm:w-6 sm:h-6 p-1" : "w-6 h-6 sm:w-8 sm:h-8"
                  }
                />
              </div>
              <div className="text-white font-semibold text-sm sm:text-lg font-roboto">
                {networkName}
              </div>
            </div>

            {/* Amount Display */}
            <div className="flex items-center gap-2">
              <div className="text-base sm:text-lg font-bold text-white font-roboto">
                {isEstimating ? <Spinner size="sm" /> : displayAmount || "0"}
              </div>
              <span className="text-gray-400 font-roboto text-sm sm:text-base">A1X</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
